// ServicesManager.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

// Importar los componentes que ya tienes
import "../style/css/ServiceList.css";
import ServiceList from "./ServiceList";
import ServiceTypeList from "./ServiceTypeList";
import api from "../api.js";
// Importar los nuevos componentes de estado
import Loader from "./Loader";
import Error403Page from "./Error_backend";

// Importar las interfaces de datos (Ajusta la ruta según tu proyecto)
import { Service, ServiceType } from "../interfaces/types";

// Definición de la estructura de respuesta de la API (Asunción)
interface ServicesApiResponse {
  services: Service[];
  serviceTypes: ServiceType[];
}

const ServicesManager: React.FC = () => {
  // --- ESTADOS ---
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("tab") === "services" ||
    searchParams.get("tab") === "service-types"
      ? (searchParams.get("tab") as "services" | "service-types")
      : "services";
  const navigate = useNavigate();

  const [services, setServices] = useState<Service[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // --- LÓGICA DE DATOS ---
  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Realizar la solicitud GET al endpoint
      const response = api.get("/services/");

      // 2. Manejo de errores de respuesta (HTTP status codes)
      if (!response) {
        // Si el status es 403, 401, o cualquier error, lanzamos un error.
        const errorMessage = `Fallo al cargar los datos`;
        return;
      }

      // 3. Procesar la respuesta
      // const data: ServicesApiResponse = { services: [], serviceTypes: [] };
      const data: ServicesApiResponse = (await response).data;
      setServices(data.services || []);
      setServiceTypes(data.serviceTypes || []);
    } catch (err) {
      // Manejo de errores de red (ej. servidor caído)
      setError(
        "Error de conexión con el servidor. Por favor, inténtelo más tarde.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Ejecutar la función de carga de datos al montar el componente
  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // --- FUNCIONES DE TABS Y ACCIONES ---
  const handleTabClick = (tabName: "services" | "service-types") => {
    setSearchParams({ tab: tabName });
  };

  const handleAddService = () => {
    navigate("/service/new");
  };

  const handleAddServiceType = () => {
    navigate("/service-types/new");
  };

  // --- RENDERIZADO CONDICIONAL ---

  // 1. Mostrar Loader si está cargando
  if (isLoading) {
    return <Loader show={true} />;
  }

  // 2. Mostrar Error Page si hay un error (ej. 403, 401)
  if (error) {
    // Renderizamos Error403Page usando las props para personalizar el error si lo detectamos
    const codeToShow = error.match(/^\d+$/) ? error : "Error";
    const titleToShow =
      codeToShow === "403" ? "Acceso Denegado" : "Error de Carga";
    const messageToShow =
      codeToShow === "403"
        ? "No tiene permiso para ver esta página. Contacte al administrador."
        : error;

    return (
      <Error403Page
        code={codeToShow}
        title={titleToShow}
        message={messageToShow}
      />
    );
  }

  // 3. Mostrar el contenido principal una vez que los datos están cargados
  return (
    <div className="container">
      {/* Controles de Pestaña (Tabs) */}
      <div className="tabs">
        <div
          className={`tab ${activeTab === "services" ? "active" : ""}`}
          data-tab="services"
          onClick={() => handleTabClick("services")}
        >
          Servicios
        </div>
        {/* <div
          className={`tab ${activeTab === "service-types" ? "active" : ""}`}
          data-tab="service-types"
          onClick={() => handleTabClick("service-types")}
        >
          Tipos de Servicios
        </div> */}
      </div>

      {/* Renderizado Condicional del Contenido, pasando los datos reales */}

      {/* Contenido para Servicios */}
      <div className={activeTab === "services" ? "" : "hidden"}>
        {/* PASAMOS LOS DATOS Y LOS TIPOS A SERVICE LIST */}
        <ServiceList
          services={services}
          serviceTypes={serviceTypes}
          onAddService={handleAddService}
        />
      </div>

      {/* Contenido para Tipos de Servicios */}
      <div className={activeTab === "service-types" ? "" : "hidden"}>
        {/* PASAMOS LOS TIPOS DE SERVICIO A SERVICE TYPE LIST */}
        <ServiceTypeList
          serviceTypes={serviceTypes}
          onAddServiceType={handleAddServiceType}
        />
      </div>
    </div>
  );
};

export default ServicesManager;
